/**
 * VideoRTC v1.6.0 — TypeScript port
 * Source: https://github.com/AlexxIT/go2rtc/blob/master/www/video-rtc.js
 * filterPrivateCandidates and resolveHostnameCandidates were added to the port
 */

declare global {
  interface Window {
    ManagedMediaSource?: typeof MediaSource
  }
}

type VideoRTCMessageHandler = (msg: { type: string; value: string }) => void

export type VideoRTCStateDetail = { status: 'connected' } | { status: 'error'; message: string }

export class VideoRTC extends HTMLElement {
  DISCONNECT_TIMEOUT = 5000
  RECONNECT_TIMEOUT = 15_000

  CODECS: string[] = [
    'avc1.640029', // H.264 high 4.1 (Chromecast 1st and 2nd Gen)
    'avc1.64002A', // H.264 high 4.2 (Chromecast 3rd Gen)
    'avc1.640033', // H.264 high 5.1 (Chromecast with Google TV)
    'hvc1.1.6.L153.B0', // H.265 main 5.1 (Chromecast Ultra)
    'mp4a.40.2', // AAC LC
    'mp4a.40.5', // AAC HE
    'flac', // FLAC (PCM compatible)
    'opus', // OPUS Chrome, Firefox
  ]

  /** Supported modes (webrtc, webrtc/tcp, mse, hls, mp4, mjpeg). */
  mode = 'webrtc,mse,hls,mjpeg'
  /** Requested medias (video, audio, microphone). */
  media = 'video,audio'

  /** Run stream when not displayed on the screen. */
  background = false

  /**
   * Run stream only when player is in the viewport.
   * Value is percentage of visibility from 0 (not visible) to 1 (full visible). Default 0 = disabled.
   */
  visibilityThreshold = 0

  /** Run stream only when browser tab is on screen. */
  visibilityCheck = true

  pcConfig: RTCConfiguration = {
    bundlePolicy: 'max-bundle',
    iceServers: [{ urls: ['stun:stun.cloudflare.com:3478', 'stun:stun.l.google.com:19302'] }],
  }

  /** WebSocket connection state. */
  wsState: number = WebSocket.CLOSED

  /** WebRTC connection state. */
  pcState: number = WebSocket.CLOSED

  video: HTMLVideoElement | null = null
  ws: WebSocket | null = null
  wsURL = ''
  pc: RTCPeerConnection | null = null
  connectTS = 0
  mseCodecs = ''
  disconnectTID = 0
  reconnectTID = 0
  ondata: ((data: ArrayBuffer) => void) | null = null
  onmessage: Record<string, VideoRTCMessageHandler> | null = null
  private _visibilityHandler: (() => void) | null = null
  private _intersectionObserver: IntersectionObserver | null = null

  /**
   * When true, skips private IP candidates sent by the server (RFC 1918 ranges).
   * Useful when the server runs inside Docker/VPN and leaks unreachable LAN IPs
   * that cause Firefox ICE to fail before reaching the public candidate.
   */
  filterPrivateCandidates = true

  /**
   * When true, resolves hostname ICE candidates to IP addresses via DNS-over-HTTPS
   * before passing them to RTCPeerConnection. Firefox requires IP addresses in
   * remote candidates and does not resolve hostnames implicitly like Chrome does.
   */
  resolveHostnameCandidates = false

  /** Set video source (WebSocket URL or HTTP URL which gets converted to WebSocket). */
  set src(value: string | URL) {
    let str = typeof value === 'string' ? value : value.toString()
    if (str.startsWith('http')) {
      str = `ws${str.substring(4)}`
    } else if (str.startsWith('/')) {
      str = `ws${location.origin.substring(4)}${str}`
    }
    this.wsURL = str
    this.onconnect()
  }

  /** Play video. Automutes when autoplay is blocked. */
  play() {
    this.video?.play().catch(() => {
      if (this.video && !this.video.muted) {
        this.video.muted = true
        this.video.play().catch((error) => console.warn(error))
      }
    })
  }

  send(value: object) {
    this.ws?.send(JSON.stringify(value))
  }

  codecs(isSupported: (type: string) => boolean): string {
    return this.CODECS.filter((codec) =>
      this.media.includes(codec.includes('vc1') ? 'video' : 'audio'),
    )
      .filter((codec) => isSupported(`video/mp4; codecs="${codec}"`))
      .join(',')
  }

  connectedCallback() {
    if (this.disconnectTID) {
      clearTimeout(this.disconnectTID)
      this.disconnectTID = 0
    }

    if (this.video) {
      const seek = this.video.seekable
      if (seek.length > 0) {
        this.video.currentTime = seek.end(seek.length - 1)
      }
      this.play()
    } else {
      this.oninit()
    }

    this.onconnect()
  }

  disconnectedCallback() {
    if (this.background || this.disconnectTID) return
    if (this.wsState === WebSocket.CLOSED && this.pcState === WebSocket.CLOSED) return

    this.disconnectTID = setTimeout(() => {
      if (this.reconnectTID) {
        clearTimeout(this.reconnectTID)
        this.reconnectTID = 0
      }
      this.disconnectTID = 0
      this.ondisconnect()
    }, this.DISCONNECT_TIMEOUT) as unknown as number
  }

  oninit() {
    this.video = document.createElement('video')
    this.video.controls = true
    this.video.playsInline = true
    this.video.preload = 'auto'
    this.video.style.display = 'block'
    this.video.style.width = '100%'
    this.video.style.height = '100%'
    this.appendChild(this.video)

    this.video.addEventListener('error', () => {
      if (this.ws) this.ws.close()
    })

    // Safari lies about supported audio codecs
    const m = window.navigator.userAgent.match(/Version\/(\d+).+Safari/)
    if (m) {
      const version = parseInt(m[1])
      const flacOrOpus = version < 14 ? 'flac' : 'opus'
      const skip = version < 13 ? 'mp4a.40.2' : flacOrOpus
      const idx = this.CODECS.indexOf(skip)
      if (idx !== -1) this.CODECS.splice(idx)
    }

    if (this.background) return

    if ('hidden' in document && this.visibilityCheck) {
      this._visibilityHandler = () => {
        if (document.hidden) {
          this.disconnectedCallback()
        } else if (this.isConnected) {
          this.connectedCallback()
        }
      }
      document.addEventListener('visibilitychange', this._visibilityHandler)
    }

    if ('IntersectionObserver' in window && this.visibilityThreshold) {
      this._intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              this.disconnectedCallback()
            } else if (this.isConnected) {
              this.connectedCallback()
            }
          })
        },
        { threshold: this.visibilityThreshold },
      )
      this._intersectionObserver.observe(this)
    }
  }

  onconnect(): boolean {
    if (!this.isConnected || !this.wsURL || this.ws || this.pc) return false

    this.wsState = WebSocket.CONNECTING
    this.connectTS = Date.now()

    this.ws = new WebSocket(this.wsURL)
    this.ws.binaryType = 'arraybuffer'
    this.ws.addEventListener('open', () => this.onopen())
    this.ws.addEventListener('close', () => this.onwsclose())

    return true
  }

  ondisconnect() {
    this.wsState = WebSocket.CLOSED
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }

    this.pcState = WebSocket.CLOSED
    if (this.pc) {
      this.pc.getSenders().forEach((sender) => sender.track?.stop())
      this.pc.close()
      this.pc = null
    }

    if (this.video) {
      this.video.removeAttribute('src')
      this.video.srcObject = null
      this.video.load()
    }
  }

  /** Immediately disconnects, bypassing the DISCONNECT_TIMEOUT delay. */
  dispose() {
    if (this.disconnectTID) {
      clearTimeout(this.disconnectTID)
      this.disconnectTID = 0
    }
    if (this.reconnectTID) {
      clearTimeout(this.reconnectTID)
      this.reconnectTID = 0
    }
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler)
      this._visibilityHandler = null
    }
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect()
      this._intersectionObserver = null
    }
    this.ondisconnect()
  }

  onopen(): string[] {
    this.wsState = WebSocket.OPEN

    this.ws?.addEventListener('message', (ev) => {
      if (typeof ev.data === 'string') {
        const msg = JSON.parse(ev.data) as { type: string; value: string }
        for (const mode in this.onmessage) {
          if (Object.hasOwn(this.onmessage, mode)) {
            this.onmessage[mode](msg)
          }
        }
      } else {
        this.ondata?.(ev.data as ArrayBuffer)
      }
    })

    this.ondata = null
    // oxlint-disable-next-line prefer-add-event-listener -- not a DOM handler; this is a Record<mode, fn> dispatcher
    this.onmessage = {}

    const modes: string[] = []

    if (this.mode.includes('mse') && ('MediaSource' in window || 'ManagedMediaSource' in window)) {
      modes.push('mse')
      this.onmse()
    } else if (
      this.mode.includes('hls') &&
      this.video?.canPlayType('application/vnd.apple.mpegurl')
    ) {
      modes.push('hls')
      this.onhls()
    } else if (this.mode.includes('mp4')) {
      modes.push('mp4')
      this.onmp4()
    }

    if (this.mode.includes('webrtc') && 'RTCPeerConnection' in window) {
      modes.push('webrtc')
      this.onwebrtc()
    }

    if (this.mode.includes('mjpeg')) {
      if (modes.length) {
        this.onmessage['mjpeg'] = (msg) => {
          if (msg.type !== 'error' || msg.value.indexOf(modes[0]) !== 0) return
          this.onmjpeg()
        }
      } else {
        modes.push('mjpeg')
        this.onmjpeg()
      }
    }

    return modes
  }

  onwsclose(): boolean {
    if (this.wsState === WebSocket.CLOSED) return false

    this.wsState = WebSocket.CONNECTING
    this.ws = null

    // WebSocket closed before WebRTC was established — surface the error to listeners.
    if (this.pcState === WebSocket.CLOSED) {
      this.dispatchEvent(
        new CustomEvent<VideoRTCStateDetail>('videortcstatechange', {
          detail: { status: 'error', message: 'Connexion au serveur de streaming impossible.' },
        }),
      )
    }

    const delay = Math.max(this.RECONNECT_TIMEOUT - (Date.now() - this.connectTS), 0)
    this.reconnectTID = setTimeout(() => {
      this.reconnectTID = 0
      this.onconnect()
    }, delay) as unknown as number

    return true
  }

  onmse() {
    // oxlint-disable-next-line init-declarations -- assigned in both branches; closure capture prevents narrowing
    let ms: MediaSource

    if (window.ManagedMediaSource) {
      const ManagedMediaSource = window.ManagedMediaSource
      ms = new ManagedMediaSource()
      ms.addEventListener(
        'sourceopen',
        () => {
          this.send({ type: 'mse', value: this.codecs(ManagedMediaSource.isTypeSupported) })
        },
        { once: true },
      )
      ;(
        this.video as HTMLVideoElement & { disableRemotePlayback?: boolean }
      ).disableRemotePlayback = true
      this.video!.srcObject = ms as unknown as MediaStream
    } else {
      ms = new MediaSource()
      ms.addEventListener(
        'sourceopen',
        () => {
          URL.revokeObjectURL(this.video!.src)
          this.send({ type: 'mse', value: this.codecs(MediaSource.isTypeSupported) })
        },
        { once: true },
      )
      this.video!.src = URL.createObjectURL(ms)
      this.video!.srcObject = null
    }

    this.play()
    this.mseCodecs = ''

    this.onmessage!['mse'] = (msg) => {
      if (msg.type !== 'mse') return

      this.mseCodecs = msg.value
      const sb = ms.addSourceBuffer(msg.value)
      sb.mode = 'segments'

      const buf = new Uint8Array(2 * 1024 * 1024)
      let bufLen = 0

      sb.addEventListener('updateend', () => {
        // Player détruit entre-temps (dispose, changement de page) : la MediaSource est fermée et
        // lire `sb.buffered` lèverait une exception.
        if (ms.readyState !== 'open') return

        if (!sb.updating && bufLen > 0) {
          try {
            sb.appendBuffer(buf.slice(0, bufLen))
            bufLen = 0
          } catch {
            // ignore buffer errors
          }
        }

        if (!sb.updating && sb.buffered?.length) {
          const end = sb.buffered.end(sb.buffered.length - 1)
          const start = end - 5
          const start0 = sb.buffered.start(0)
          if (start > start0) {
            sb.remove(start0, start)
            ms.setLiveSeekableRange(start, end)
          }
          if (this.video!.currentTime < start) {
            this.video!.currentTime = start
          }
          const gap = end - this.video!.currentTime
          this.video!.playbackRate = gap > 0.1 ? gap : 0.1
        }
      })

      this.ondata = (data) => {
        if (sb.updating || bufLen > 0) {
          const b = new Uint8Array(data)
          buf.set(b, bufLen)
          bufLen += b.byteLength
        } else {
          try {
            sb.appendBuffer(data)
          } catch {
            // ignore buffer errors
          }
        }
      }
    }
  }

  onwebrtc() {
    const pc = new RTCPeerConnection(this.pcConfig)

    pc.addEventListener('icecandidate', (ev) => {
      if (ev.candidate && this.mode.includes('webrtc/tcp') && ev.candidate.protocol === 'udp')
        return
      const candidate = ev.candidate ? ev.candidate.toJSON().candidate : ''
      this.send({ type: 'webrtc/candidate', value: candidate })
    })

    pc.addEventListener('connectionstatechange', () => {
      if (pc.connectionState === 'connected') {
        const tracks = pc
          .getTransceivers()
          .filter((tr) => tr.currentDirection === 'recvonly')
          .map((tr) => tr.receiver.track)

        const video2 = document.createElement('video')
        video2.addEventListener('loadeddata', () => this.onpcvideo(video2), { once: true })
        video2.srcObject = new MediaStream(tracks)
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        pc.close()

        this.pcState = WebSocket.CLOSED
        this.pc = null

        this.dispatchEvent(
          new CustomEvent<VideoRTCStateDetail>('videortcstatechange', {
            detail: {
              status: 'error',
              message: 'La connexion WebRTC a été interrompue. Relancez la lecture.',
            },
          }),
        )

        this.onconnect()
      }
    })

    this.onmessage!['webrtc'] = (msg) => {
      switch (msg.type) {
        case 'webrtc/candidate': {
          if (this.mode.includes('webrtc/tcp') && msg.value.includes(' udp ')) return
          if (this.filterPrivateCandidates && isPrivateIpCandidate(msg.value)) return
          const addCandidate = (value: string) =>
            pc
              .addIceCandidate({ candidate: value, sdpMid: '0' })
              .catch((error) => console.warn(error))
          if (this.resolveHostnameCandidates) {
            resolveHostnameInCandidate(msg.value).then(addCandidate)
          } else {
            addCandidate(msg.value)
          }
          break
        }
        case 'webrtc/answer': {
          pc.setRemoteDescription({ type: 'answer', sdp: msg.value }).catch((error) =>
            console.warn(error),
          )
          break
        }
        case 'error': {
          if (!msg.value.includes('webrtc/offer')) return
          pc.close()
        }
      }
    }

    void this.createOffer(pc).then((offer) => {
      this.send({ type: 'webrtc/offer', value: offer.sdp })
    })

    this.pcState = WebSocket.CONNECTING
    this.pc = pc
  }

  async createOffer(pc: RTCPeerConnection): Promise<RTCSessionDescriptionInit> {
    try {
      if (this.media.includes('microphone')) {
        const media = await navigator.mediaDevices.getUserMedia({ audio: true })
        media.getTracks().forEach((track) => pc.addTransceiver(track, { direction: 'sendonly' }))
      }
    } catch (error) {
      console.warn(error)
    }

    for (const kind of ['video', 'audio'] as const) {
      if (this.media.includes(kind)) {
        pc.addTransceiver(kind, { direction: 'recvonly' })
      }
    }

    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    return offer
  }

  onpcvideo(video2: HTMLVideoElement) {
    if (!this.pc) {
      video2.srcObject = null
      return
    }

    // Video+Audio > Video, H265 > H264, Video > Audio, WebRTC > MSE
    let rtcPriority = 0,
      msePriority = 0

    const stream = video2.srcObject as MediaStream
    if (stream.getVideoTracks().length > 0) {
      const isH265 = this.pc.remoteDescription?.sdp.includes('H265/90000') ?? false
      rtcPriority += isH265 ? 0x2_40 : 0x2_20
    }
    if (stream.getAudioTracks().length > 0) rtcPriority += 0x1_02

    if (this.mseCodecs.includes('hvc1.')) msePriority += 0x2_30
    if (this.mseCodecs.includes('avc1.')) msePriority += 0x2_10
    if (this.mseCodecs.includes('mp4a.')) msePriority += 0x1_01

    if (rtcPriority >= msePriority) {
      this.video!.srcObject = stream
      this.play()

      this.pcState = WebSocket.OPEN
      this.wsState = WebSocket.CLOSED
      if (this.ws) {
        this.ws.close()
        this.ws = null
      }

      this.dispatchEvent(
        new CustomEvent<VideoRTCStateDetail>('videortcstatechange', {
          detail: { status: 'connected' },
        }),
      )
    } else {
      this.pcState = WebSocket.CLOSED
      if (this.pc) {
        this.pc.close()
        this.pc = null
      }
    }

    video2.srcObject = null
  }

  onmjpeg() {
    this.ondata = (data) => {
      if (!this.video) return
      this.video.controls = false
      this.video.poster = `data:image/jpeg;base64,${VideoRTC.btoa(data)}`
    }
    this.send({ type: 'mjpeg' })
  }

  onhls() {
    this.onmessage!['hls'] = (msg) => {
      if (msg.type !== 'hls') return
      const url = `http${this.wsURL.substring(2, this.wsURL.indexOf('/ws'))}/hls/`
      const playlist = msg.value.replace('hls/', url)
      this.video!.src = `data:application/vnd.apple.mpegurl;base64,${btoa(playlist)}`
      this.play()
    }
    this.send({
      type: 'hls',
      value: this.codecs((type) => this.video?.canPlayType(type) !== ''),
    })
  }

  onmp4() {
    const canvas = document.createElement('canvas')
    let context: CanvasRenderingContext2D | null = null

    const video2 = document.createElement('video')
    video2.autoplay = true
    video2.playsInline = true
    video2.muted = true

    video2.addEventListener('loadeddata', () => {
      if (!context) {
        canvas.width = video2.videoWidth
        canvas.height = video2.videoHeight
        context = canvas.getContext('2d')
      }
      context?.drawImage(video2, 0, 0, canvas.width, canvas.height)
      if (this.video) {
        this.video.controls = false
        this.video.poster = canvas.toDataURL('image/jpeg')
      }
    })

    this.ondata = (data) => {
      video2.src = `data:video/mp4;base64,${VideoRTC.btoa(data)}`
    }

    this.send({
      type: 'mp4',
      value: this.codecs((type) => !!this.video?.canPlayType(type)),
    })
  }

  static btoa(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return window.btoa(binary)
  }
}

// RFC 1918 private ranges: 10.x, 172.16-31.x, 192.168.x
const PRIVATE_IP_RE =
  /\s(?:10\.\d+\.\d+\.\d+|172\.(?:1[6-9]|2\d|3[01])\.\d+\.\d+|192\.168\.\d+\.\d+)\s/

function isPrivateIpCandidate(candidate: string): boolean {
  return PRIVATE_IP_RE.test(candidate)
}

// ICE candidate token at index 4 is the connection address.
// e.g. "candidate:123 1 udp 2130706429 store-101.example.com 8555 typ host"
// ! This is temporary fix code until the go2rtc config is updated to return an ip instead of domain name
async function resolveHostnameInCandidate(candidate: string): Promise<string> {
  const tokens = candidate.split(' ')
  const addr = tokens[4]
  if (!addr) return candidate

  // Already an IPv4 or IPv6 address — nothing to resolve.
  if (/^\d+\.\d+\.\d+\.\d+$/.test(addr) || addr.includes(':')) return candidate

  try {
    const res = await fetch(
      `https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(addr)}&type=A`,
      { headers: { Accept: 'application/dns-json' } },
    )
    const data = (await res.json()) as { Answer?: { type: number; data: string }[] }
    const ip = data.Answer?.find((a) => a.type === 1)?.data
    if (ip) {
      tokens[4] = ip
      return tokens.join(' ')
    }
  } catch {
    // Fall through and use the hostname as-is.
  }

  return candidate
}
